import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from 'store';
import {
    BasicFormWithAlerts,
    CustomFormItem,
    DangerTag,
    FormItem,
    IconTextButton,
    MultipleSelectFormItem,
    notification,
    SelectFormItemWithSkeleton,
    Skeleton,
    Spinner,
    SuccessTag,
    TextFieldItem,
    toSelectItemFn,
    useForm,
} from 'ui-lib';
import { OrganisationSelectItem } from 'pages/private/components/organisation-select';
import { ISnapshotProps } from 'dto/software-snapshot-dto';
import { PSP } from 'dto/key-management/key-dto';
import { pspTypesForClient } from 'pages/private/keys/key-utils';
import { useAlertMessages } from 'pages/private/components/alert/use-messages';
import { WelcomePackStatus, WelcomePackStepKey, WelcomePackStepType } from 'dto/welcome-pack.dto';
import { getErrorMessage } from 'common-utils';
import welcomePackCss from './welcome-pack.css';
import { useLoadingStates } from 'hooks/use-loading.hook';
import { useWelcomePackQueryManager, WelcomePackUtils } from './utils';

interface IWelcomePackDispatch {
    type: WelcomePackStepKey | 'label';
    payload: any;
}

interface IWelcomePackConfiguration {
    baseReleaseId: number | undefined;
    selectedPsps: PSP[];
    afcReleaseId: number | undefined;
    label: string;
}

const mandatoryStepConfigurations = [WelcomePackStepKey.BASE_RELEASE, WelcomePackStepKey.KEY_DISTRIBUTION];
const isRequiredConfiguration = (key: WelcomePackStepKey) => {
    return mandatoryStepConfigurations.includes(key);
};

export const SaveWelcomePack = observer(() => {
    const { accountStore, welcomePackStore, snapshotStore, keyStore } = useStores();

    const {
        errorMessages,
        warningMessages,
        clearErrorMessages,
        addErrorMessages,
        addWarningMessages,
        clearWarningMessages,
    } = useAlertMessages();

    const {
        welcomePack,
        accountSelector,
        loading: welcomePackLoading,
        onBackToWelcomePacks,
    } = useWelcomePackQueryManager();

    const { saving, withSaving } = useLoadingStates();

    const [snapshotDataSource, setSnapshotDataSource] = React.useState<ISnapshotProps[]>([]);

    React.useEffect(() => {
        if (!accountSelector.selectedAccount) {
            setSnapshotDataSource([]);
            return;
        }

        snapshotStore.loadEntities(accountSelector.selectedAccount.id).then(() => {
            setSnapshotDataSource(snapshotStore.getSnapshotsByAccountUuid(accountSelector.selectedAccount.uuid));
        });
    }, [accountSelector.selectedAccount]);

    const isExisting = React.useMemo(() => {
        clearWarningMessages();
        if (!welcomePack) {
            addWarningMessages(['Once the welcome pack is created, it cannot be edited.']);
            return false;
        }
        addWarningMessages(['The active welcome pack cannot be edited.']);
        return true;
    }, [welcomePack]);

    const allowDeactivate = React.useMemo(() => {
        if (!welcomePack) {
            return false;
        }
        return welcomePack.status === WelcomePackStatus.ACTIVE;
    }, [welcomePack]);

    const [form] = useForm();

    const [configuration, dispatch] = React.useReducer<React.Reducer<IWelcomePackConfiguration, IWelcomePackDispatch>>(
        (state: IWelcomePackConfiguration, action: IWelcomePackDispatch): IWelcomePackConfiguration => {
            switch (action.type) {
                case 'label':
                    return { ...state, label: action.payload };
                case WelcomePackStepKey.BASE_RELEASE:
                    return { ...state, baseReleaseId: action.payload };
                case WelcomePackStepKey.KEY_DISTRIBUTION:
                    return { ...state, selectedPsps: action.payload };
                case WelcomePackStepKey.AFC_UPDATE:
                    return { ...state, afcReleaseId: action.payload };
                default:
                    return state;
            }
        },
        {
            label: '',
            baseReleaseId: undefined,
            selectedPsps: [],
            afcReleaseId: undefined,
        },
    );

    const { pspEncryptionTypeMap, isLoading: pspLoading } = pspTypesForClient(
        accountStore,
        keyStore,
        accountSelector.selectedAccount?.id,
        {
            fullyKeyConfigured: true,
            supportPspTypes: WelcomePackUtils.SUPPORTED_PSP_TYPES,
        },
    );

    React.useEffect(() => {
        dispatch({ type: 'label', payload: welcomePack?.label });
        dispatch({ type: WelcomePackStepKey.BASE_RELEASE, payload: welcomePack?.baseRelease?.releaseId });
        dispatch({ type: WelcomePackStepKey.KEY_DISTRIBUTION, payload: welcomePack?.keyDistribution?.pspKeys });
        dispatch({ type: WelcomePackStepKey.AFC_UPDATE, payload: welcomePack?.afcUpdate?.releaseId });
    }, [welcomePack]);

    React.useEffect(() => {
        form.setFieldsValue({
            label: configuration?.label,
            [WelcomePackStepKey.BASE_RELEASE]: configuration?.baseReleaseId,
            [WelcomePackStepKey.KEY_DISTRIBUTION]: configuration?.selectedPsps,
            [WelcomePackStepKey.AFC_UPDATE]: configuration?.afcReleaseId,
        });
    }, [configuration]);

    async function onSave() {
        clearErrorMessages();
        try {
            if (!accountSelector.selectedAccount) {
                throw new Error('The client account is not selected.');
            }
            const requiredConfigurations = [
                {
                    key: WelcomePackStepKey.BASE_RELEASE,
                    type: WelcomePackStepType.SOFTWARE_UPDATE,
                    releaseId: configuration.baseReleaseId,
                },
                {
                    key: WelcomePackStepKey.KEY_DISTRIBUTION,
                    type: WelcomePackStepType.KEY_DISTRIBUTION,
                    pspKeys: configuration.selectedPsps,
                },
            ];
            if (configuration.afcReleaseId) {
                requiredConfigurations.push({
                    key: WelcomePackStepKey.AFC_UPDATE,
                    type: WelcomePackStepType.SOFTWARE_UPDATE,
                    releaseId: configuration.afcReleaseId,
                });
            }
            await welcomePackStore.createWelcomePack(
                accountSelector.selectedAccount?.id,
                accountSelector.selectedAccount?.uuid,
                {
                    label: configuration.label,
                    stepConfigurations: requiredConfigurations,
                },
            );
            onBackToWelcomePacks(() =>
                notification.success({ message: 'Welcome pack', description: `The welcome pack has been created.` }),
            );
        } catch (err) {
            addErrorMessages(getErrorMessage(err));
        }
    }

    return (
        <Spinner spinning={saving} className={welcomePackCss['loading-spinner']}>
            <Skeleton loading={welcomePackLoading}>
                <BasicFormWithAlerts
                    errorMessages={errorMessages}
                    warningMessages={warningMessages}
                    title='Welcome Pack Configuration'
                    form={form}
                    onSubmit={() => {
                        WelcomePackUtils.showCreateConfirmDialog(async () => {
                            withSaving(onSave);
                        });
                    }}
                    cardPros={{
                        title: welcomePack?.label ?? 'New Welcome Pack',
                        extra: !allowDeactivate ? null : (
                            <IconTextButton
                                label='Deactivate'
                                type='default'
                                onClick={() => {
                                    clearErrorMessages();
                                    WelcomePackUtils.showDeactivateConfirmDialog(async (force?: boolean) => {
                                        try {
                                            const result = (
                                                await withSaving(() =>
                                                    welcomePackStore.inactivateWelcomePack(
                                                        accountSelector.selectedAccount?.id,
                                                        welcomePack?.accountUuid,
                                                        welcomePack?.id,
                                                        force,
                                                    ),
                                                )
                                            ).getOrThrow();
                                            if (result.updated) {
                                                onBackToWelcomePacks(() =>
                                                    notification.success({
                                                        message: 'Deactivate Welcome Pack',
                                                        description: `The welcome pack has been deactivated.`,
                                                    }),
                                                );
                                            }
                                        } catch (err) {
                                            notification.error({
                                                message: 'Error',
                                                description: getErrorMessage(err),
                                            });
                                        }
                                    });
                                }}
                            />
                        ),
                    }}
                    items={[
                        <OrganisationSelectItem
                            code='clientField'
                            label='Client'
                            colon={true}
                            labelAlign='right'
                            initialValue={accountSelector.selectedAccount}
                            isRequired={true}
                            disabled={isExisting && !allowDeactivate}
                            selectProps={{
                                treeDataSource: accountSelector.dataSource,
                                expandAll: true,
                                value: accountSelector.selectedAccount?.id,
                                onChange: accountSelector.onChange,
                            }}
                        />,
                        <TextFieldItem
                            code='label'
                            label='Label'
                            colon={true}
                            isRequired={true}
                            disabled={isExisting}
                            initialValue={configuration.label}
                            value={configuration.label}
                            onChange={(field) => dispatch({ type: 'label', payload: field?.target?.value })}
                            labelAlign='right'
                        />,
                        <CustomFormItem
                            label='Status'
                            code='status'
                            labelAlign='right'
                            colon={true}
                            hidden={!welcomePack}
                        >
                            <div className={welcomePackCss['status-tag']}>
                                {WelcomePackUtils.isActive(welcomePack) ? (
                                    <SuccessTag value={welcomePack?.status} />
                                ) : (
                                    <DangerTag value={welcomePack?.status} />
                                )}
                            </div>
                        </CustomFormItem>,
                        <SelectFormItemWithSkeleton
                            code={WelcomePackStepKey.BASE_RELEASE}
                            label='Base Release'
                            labelAlign='right'
                            colon={true}
                            isRequired={isRequiredConfiguration(WelcomePackStepKey.BASE_RELEASE)}
                            loading={snapshotStore.dataLoading}
                            disabled={isExisting}
                            dataSource={snapshotDataSource.map(toSelectItemFn('id', 'label'))}
                            value={configuration.baseReleaseId}
                            onChange={(id: number): void => {
                                dispatch({ type: WelcomePackStepKey.BASE_RELEASE, payload: id });
                            }}
                        />,
                        <MultipleSelectFormItem
                            code={WelcomePackStepKey.KEY_DISTRIBUTION}
                            label='PSPs for key distribution (excl. Vault)'
                            labelAlign='right'
                            colon={true}
                            loading={pspLoading}
                            isRequired={isRequiredConfiguration(WelcomePackStepKey.KEY_DISTRIBUTION)}
                            disabled={isExisting}
                            dataSource={Array.from(pspEncryptionTypeMap.keys())
                                .map((pspType) => ({
                                    value: pspType,
                                    label: pspType,
                                }))}
                            selectedValue={configuration.selectedPsps}
                            onChange={(selectedPsps) =>
                                dispatch({ type: WelcomePackStepKey.KEY_DISTRIBUTION, payload: selectedPsps })
                            }
                        />,
                        <SelectFormItemWithSkeleton
                            code={WelcomePackStepKey.AFC_UPDATE}
                            label='AFC Release'
                            labelAlign='right'
                            colon={true}
                            isRequired={isRequiredConfiguration(WelcomePackStepKey.AFC_UPDATE)}
                            loading={snapshotStore.dataLoading}
                            disabled={isExisting}
                            dataSource={snapshotDataSource.map(toSelectItemFn('id', 'label'))}
                            value={configuration.afcReleaseId}
                            onChange={(id: number): void => {
                                dispatch({ type: WelcomePackStepKey.AFC_UPDATE, payload: id });
                            }}
                        />,
                        <FormItem
                            code='footer'
                            child={
                                <div>
                                    <IconTextButton
                                        className={welcomePackCss['footer-left-button']}
                                        htmlType='button'
                                        type='default'
                                        label='Back to welcome packs'
                                        onClick={onBackToWelcomePacks}
                                    />
                                    <IconTextButton
                                        className={welcomePackCss['footer-right-button']}
                                        htmlType='submit'
                                        type='primary'
                                        hidden={isExisting}
                                        label='Finish'
                                    />
                                </div>
                            }
                        />,
                    ]}
                />
            </Skeleton>
        </Spinner>
    );
});
